import React, { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'

import { TrashAPI } from '@makyoapp/makyo-sdk'
import { fromSdkTrashList, TrashItemDetail, TrashItemKind, TrashItemTy } from '@makyoapp/makyo-common'

import constate from '@/lib/constate'
import { queryKeys } from '@/lib/react-query/query-keys'
import { toasted } from '@/lib/toast'
import { useGetMyTrashList } from '@/resolvers/user/query'
import { useQueryParam } from '@/shared/makyo-hooks'

import { useReqError } from '../hooks/useReqError'
import { useOrgsContext } from './OrgsContext'

const [MyTrashProvider, useMyTrashContext] = constate('MyTrashContext', () => {
  const { t } = useTranslation()
  const printAPIError = useReqError()
  const queryClient = useQueryClient()

  const { updateRecentApps, updateOrgs } = useOrgsContext()
  const myTrashQuery = useGetMyTrashList()

  const allTrashes = useMemo(() => {
    return {
      items: fromSdkTrashList(myTrashQuery.data || []),
      totalCount: myTrashQuery.data?.length || 0,
    }
  }, [myTrashQuery.data])

  const [checks, setChecks] = useState<TrashItemTy<TrashItemDetail>[]>([])
  const [trashes, setTrashes] = useState<TrashItemTy<TrashItemDetail>[]>([])

  const { isLoading, setIsLoading, page, setPage } = useQueryParam()

  const handleRestore = useCallback(
    (items: TrashItemTy<TrashItemDetail>[]) => {
      const toBeRestored = items.map((c) => c.id)

      setIsLoading(true)

      TrashAPI.ReqRestore({
        _TrashIDs: toBeRestored,
      })
        .then(() => {
          queryClient.invalidateQueries({ queryKey: [queryKeys.user.trash] })

          const hasApps = items.filter((i) => i.type === TrashItemKind.App).length > 0
          const hasOrgs = items.filter((i) => i.type === TrashItemKind.Org).length > 0
          const hasTeams = items.filter((i) => i.type === TrashItemKind.Team).length > 0

          if (hasApps) {
            updateRecentApps()
          }
          if (hasOrgs || hasTeams) {
            updateOrgs().catch(() => {
              console.error(`update orgs from trash failed`)
            })
          }
          if (hasTeams) {
            queryClient.invalidateQueries({ queryKey: [queryKeys.user.teams] })
            queryClient.invalidateQueries({ queryKey: [queryKeys.orgTeams] })
          }

          toasted(t('App restored from trash'))
        })
        .catch(printAPIError)
        .finally(() => {
          setIsLoading(false)
          setChecks([])
        })
    },
    [updateRecentApps, updateOrgs, setIsLoading, printAPIError, setChecks, queryClient, t]
  )

  const handleDel = useCallback(
    (
      items: TrashItemTy<TrashItemDetail>[],
      options?: {
        bulk: boolean
        onStart?: () => void
        onFinally?: () => void
      }
    ) => {
      const toBeRemoved = items.map((i) => i.id)

      options?.onStart?.()
      TrashAPI.ReqPermanentlyRemove({
        _TrashIDs: toBeRemoved,
      })
        .then(() => {
          queryClient.invalidateQueries({ queryKey: [queryKeys.user.trash] })
          toasted(t('App permanently removed from trash'))
        })
        .catch(printAPIError)
        .finally(() => {
          options?.onFinally?.()
          setChecks([])
        })
    },
    [printAPIError, setChecks, queryClient, t]
  )

  return {
    allTrashes,
    trashes,
    setTrashes,
    checks,
    setChecks,
    isLoading,
    setIsLoading,
    page,
    setPage,
    handleRestore,
    handleDel,
  }
})

export { MyTrashProvider, useMyTrashContext }
